import { type FC, useMemo } from 'react'
import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next'
import { type DehydratedState, QueryClient, dehydrate } from '@tanstack/react-query'

import { CatalogIntegrationView } from 'views/Catalog'

import {
  createAllIntegrationsQuery,
  createSingleIntegrationQuery,
  createSimilarIntegrationsQuery,
} from '@segment/api/routes/catalog/queries'

import { createClient } from '@segment/api/algolia/client'

import { CatalogClientProvider, createCatalogAlphaIndex } from '@segment/contexts/catalogIndex'
import { createStaticSiteBackend, StaticSiteBackendProvider } from '@segment/contexts/staticSiteBackend'

import type { AppExternalPageProps } from '@segment/next-shared/Providers'

interface IntegrationProps {
  slug: string
  category: string
  random: boolean
  amount: number
}
const Integration: FC<InferGetStaticPropsType<typeof getStaticProps>> = ({ slug, category, random, amount }) => {
  const catalogClient = createClient()
  const staticSiteBackend = useMemo(() => createStaticSiteBackend(), [])

  return (
    <StaticSiteBackendProvider value={staticSiteBackend}>
      <CatalogClientProvider value={catalogClient}>
        <CatalogIntegrationView slug={slug} category={category} random={random} amount={amount} />
      </CatalogClientProvider>
    </StaticSiteBackendProvider>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const staticSiteBackend = createStaticSiteBackend()
  const [, queryFn] = createAllIntegrationsQuery(staticSiteBackend)
  const integrations = await queryFn()

  const integrationsWithTypes = integrations
    .filter((data) => data.integrationType !== undefined)
    .map((category) => ({ params: { slug: [String(category.integrationType), String(category.url)] } }))

  const oldIntegrations = integrations.map((category) => ({ params: { slug: [String(category.url)] } }))

  const mergedIntegrations = [...integrationsWithTypes, ...oldIntegrations]

  return {
    paths: mergedIntegrations,
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps<AppExternalPageProps & IntegrationProps> = async ({ params }) => {
  const queryClient = new QueryClient()
  const staticSiteBackend = createStaticSiteBackend()
  const algoliaClient = createClient()
  const catalogIndex = createCatalogAlphaIndex(algoliaClient)

  if (!params?.slug) {
    return {
      props: {} as AppExternalPageProps & IntegrationProps,
    }
  }

  const slug = params?.slug?.length > 1 ? String(params?.slug[1]) : String(params?.slug[0])

  const singleIntegration = await queryClient.fetchQuery(...createSingleIntegrationQuery(staticSiteBackend, slug), {
    staleTime: Infinity,
  })

  if (!singleIntegration) {
    return {
      props: {} as AppExternalPageProps & IntegrationProps,
    }
  }

  const baseProps: IntegrationProps = {
    slug,
    random: true,
    amount: 4,
    category: singleIntegration?.category[0]?.fields.url,
  }

  await queryClient.prefetchQuery(
    ...createSimilarIntegrationsQuery(catalogIndex, {
      slug: baseProps.slug,
      random: true,
      amount: 4,
      category: baseProps.category,
    }),
    {
      staleTime: Infinity,
    }
  )

  const dangerousDehydratedState = dehydrate(queryClient)
  const dehydratedState: DehydratedState = JSON.parse(JSON.stringify(dangerousDehydratedState))

  const props: AppExternalPageProps & IntegrationProps = {
    ...baseProps,
    dehydratedState,
  }

  return {
    props,
  }
}

export default Integration
